import { memo, MouseEvent, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { Button } from "@design-system/Button/Button";
import { markEventBlocked } from "@design-system/ViewTable/ViewTable.lib";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { ArrowCornerIcon } from "@icons";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import styles from "../cells/HistoryDetailsCells.module.scss";
import { ScheduleActivityRow } from "./scheduleActivityRows";

export const ScheduleActivityTextCell = memo(
    ({
        getValue,
    }: CellContext<ViewTableFeatures, ScheduleActivityRow, string | null>) => {
        const value = getValue();
        return (
            <ViewTableCell>
                {value ? (
                    <BreakableText text={value} showTooltip />
                ) : (
                    <EmptyValue />
                )}
            </ViewTableCell>
        );
    },
);

ScheduleActivityTextCell.displayName = "ScheduleActivityTextCell";

export const ScheduleActivityArrowCell = memo(
    ({ row }: CellContext<ViewTableFeatures, ScheduleActivityRow, string>) => {
        const navigate = useStateAwareNavigate();
        const { link } = row.original;

        const handleClick = useCallback(
            (event: MouseEvent<HTMLButtonElement>) => {
                markEventBlocked(event);
                if (link) {
                    navigate(link);
                }
            },
            [link, navigate],
        );

        if (!link) {
            return <ViewTableCell className={styles.actions} />;
        }

        return (
            <ViewTableCell className={styles.actions}>
                <div className={styles.actionsButtons}>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleClick}
                        aria-label="View details"
                        iconOnly
                        className={tableStyles.showOnHover}
                    >
                        <ArrowCornerIcon />
                    </Button>
                </div>
            </ViewTableCell>
        );
    },
);

ScheduleActivityArrowCell.displayName = "ScheduleActivityArrowCell";
